// @ts-nocheck - Amplify Gen 2 type compatibility issues
/**
 * Helper Functions for Energy Tariff Calculator
 *
 * @description Utility functions shared across all tariff calculators
 * @version 2.1.0
 * @created 2025-01-26
 * @author eCompanion2 Energy Tariff Calculator
 *
 * Extracted from tariff-calculator.ts for better modularity
 */

import { getDayType } from '../polish-holidays';
import type { ConsumptionPattern, HourlyConsumption } from './types';
import { DAYS_IN_MONTH, DAYS_IN_MONTH_LEAP } from './constants';

// ============================================================================
// SEASONAL HELPERS
// ============================================================================

/**
 * Determine if month is in winter season (Oct-Mar)
 * @param month - Month (0-indexed, 0=Jan)
 * @returns true if winter season
 */
export function isWinterSeason(month: number): boolean {
  return month >= 9 || month <= 2; // Oct (9) - Mar (2)
}

// ============================================================================
// NUMBER UTILITIES
// ============================================================================

/**
 * Round number to 2 decimal places (IEEE 754 safe)
 *
 * Uses exponential notation to avoid floating-point precision errors.
 * Example: Math.round(1.005 * 100) / 100 = 1.00 (wrong)
 *          This function correctly returns 1.01
 *
 * @param value - Number to round
 * @returns Rounded value
 */
export function roundToTwoDecimals(value: number): number {
  return Number(Math.round(parseFloat(value + 'e2')) + 'e-2');
}

/**
 * Get number of days in month
 * @param year - Year (must be integer between 1900-2100)
 * @param month - Month (0-indexed, 0=Jan, must be integer 0-11)
 * @returns Number of days
 * @throws Error if year or month is invalid
 */
export function getDaysInMonth(year: number, month: number): number {
  // HIGH Security: Validate year boundary
  if (!Number.isInteger(year) || year < 1900 || year > 2100) {
    throw new Error('Year must be an integer between 1900 and 2100');
  }

  // HIGH Security: Validate month boundary
  if (!Number.isInteger(month) || month < 0 || month > 11) {
    throw new Error('Month must be an integer between 0 and 11');
  }

  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysPerMonth = isLeapYear ? DAYS_IN_MONTH_LEAP : DAYS_IN_MONTH;
  return daysPerMonth[month];
}

// ============================================================================
// CONSUMPTION DISTRIBUTION
// ============================================================================

/**
 * Distribute monthly consumption across hours based on pattern
 *
 * Algorithm:
 * 1. Calculate base hourly consumption (monthlyTotal / daysInMonth / 24)
 * 2. Apply pattern multipliers to each hour
 * 3. Normalize to match exact monthly total
 *
 * @param monthlyTotal - Total kWh for month
 * @param pattern - Hourly multipliers (weekday/weekend)
 * @param daysInMonth - Days in month
 * @param year - Year (for holiday detection)
 * @param month - Month (0-indexed, 0=Jan)
 * @returns Array of hourly consumption data
 *
 * @example
 * const hourly = distributeConsumption(300, UNIFORM_PATTERN, 31, 2025, 0);
 * // Returns: [{ hour: 0, dayOfMonth: 1, consumption: 0.403, isWeekend: false, isHoliday: true }, ...]
 */
export function distributeConsumption(
  monthlyTotal: number,
  pattern: ConsumptionPattern,
  daysInMonth: number,
  year: number,
  month: number
): HourlyConsumption[] {
  // Validate pattern array lengths
  if (pattern.weekday.length !== 24 || pattern.weekend.length !== 24) {
    throw new Error('Pattern must have exactly 24 hourly values for weekday and weekend');
  }

  // CRITICAL Security: Validate pattern values are non-negative
  const hasNegativeWeekday = pattern.weekday.some((val) => val < 0);
  const hasNegativeWeekend = pattern.weekend.some((val) => val < 0);
  if (hasNegativeWeekday || hasNegativeWeekend) {
    throw new Error('Pattern values must be non-negative');
  }

  // CRITICAL Security: Check for all-zero pattern (would cause division by zero)
  const weekdaySum = pattern.weekday.reduce((sum, val) => sum + val, 0);
  const weekendSum = pattern.weekend.reduce((sum, val) => sum + val, 0);
  if (weekdaySum === 0 && weekendSum === 0) {
    throw new Error('Pattern must have at least one non-zero value');
  }

  const result: HourlyConsumption[] = [];

  // Handle zero consumption edge case
  if (monthlyTotal === 0) {
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayType = getDayType(date);
      const isWeekend = dayType === 'weekend';
      const isHoliday = dayType === 'holiday';

      for (let hour = 0; hour < 24; hour++) {
        result.push({
          hour,
          dayOfMonth: day,
          consumption: 0,
          isWeekend,
          isHoliday,
          dayOfWeek: date.getDay()
        });
      }
    }
    return result;
  }

  // Step 1: Calculate base hourly consumption
  const baseHourly = monthlyTotal / daysInMonth / 24;

  // Step 2: Apply pattern multipliers and calculate total weight
  let totalWeightedHours = 0;

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dayType = getDayType(date);
    const isWeekend = dayType === 'weekend';
    const isHoliday = dayType === 'holiday';
    const patternArray = (isWeekend || isHoliday) ? pattern.weekend : pattern.weekday;

    for (let hour = 0; hour < 24; hour++) {
      totalWeightedHours += patternArray[hour];
    }
  }

  // CRITICAL Security: Prevent division by zero if all applicable pattern values are zero
  if (totalWeightedHours === 0) {
    throw new Error('Pattern must have at least one non-zero value');
  }

  // Step 3: Calculate normalized consumption for each hour
  const normalizationFactor = monthlyTotal / (baseHourly * totalWeightedHours);
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dayType = getDayType(date);
    const isWeekend = dayType === 'weekend';
    const isHoliday = dayType === 'holiday';
    const patternArray = (isWeekend || isHoliday) ? pattern.weekend : pattern.weekday;

    for (let hour = 0; hour < 24; hour++) {
      const consumption = baseHourly * patternArray[hour] * normalizationFactor;

      result.push({
        hour,
        dayOfMonth: day,
        consumption,
        isWeekend,
        isHoliday,
        dayOfWeek: date.getDay()
      });
    }
  }

  return result;
}
